import React, { useState, useEffect } from "react";
import {
  XMarkIcon,
  PlayCircleIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";

import Loading from "../loading";
import { getAllLessons } from "../../api/lesson";
import { getStatementsByLesson } from "../../api/evaluationApi";
import ArticleInlineEditor from "../articleInlineEditor";
import { createhomeWorkQuestion } from "../../api/homeWork";
import { uploadQuestionsImage } from "../../api/Question";
import HomeWorksTable from "../tables/homeWorksTable";
import Navigation from "../Navigation";
import GetAdmin from "../../utils/getAdmin";
import Header from "../Header";

const INITIAL_QUESTION = {
  order: 1,
  hasEducationalContent: false,
  hasQuestion: true,
  questionText: "",
  options: [
    { optionId: "1", optionText: "" },
    { optionId: "2", optionText: "" },
    { optionId: "3", optionText: "" },
    { optionId: "4", optionText: "" },
  ],
  correctAnswer: "",
  defaultScore: 1,
  educationalContent: "",
  educationalImage: "",
  videoUrl: "",
  statementId: "",
  guide: "",
  answerDescription: "",
};

const GRADE_MAP = {
  1: "پایه اول",
  2: "پایه دوم",
  3: "پایه سوم",
  4: "پایه چهارم",
  5: "پایه پنجم",
  6: "پایه ششم",
};

const BOOK_MAP = {
  1: "ریاضی",
  2: "فارسی و نگارش",
  3: "علوم",
  4: "قرآن",
  5: "املا",
  6: "هدیه‌های آسمانی",
  7: "مطالعات اجتماعی",
};

const SLIDE_TYPES = [
  {
    value: "question",
    label: "سؤال",
    description: "ثبت سؤال چهارگزینه‌ای",
    active: "bg-blue-600 text-white border-blue-600",
  },
  {
    value: "education",
    label: "آموزش",
    description: "متن، تصویر یا ویدیو",
    active: "bg-purple-600 text-white border-purple-600",
  },
  {
    value: "content",
    label: "محتوای درسی",
    description: "متن، تصویر و ویدیوی درس",
    active: "bg-emerald-600 text-white border-emerald-600",
  },
];

const HomeWork = () => {
  const [adminData, setAdminData] = useState(null);

  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedBook, setSelectedBook] = useState("");
  const [selectedLesson, setSelectedLesson] = useState("");
  const [difficulty, setDifficulty] = useState("medium");

  const [slideType, setSlideType] = useState("question");
  const [currentQuestion, setCurrentQuestion] = useState({
    ...INITIAL_QUESTION,
    options: INITIAL_QUESTION.options.map((option) => ({ ...option })),
  });

  const [allLessons, setAllLessons] = useState([]);
  const [filteredLessons, setFilteredLessons] = useState([]);

  const [statements, setStatements] = useState([]);
  const [statementsLoading, setStatementsLoading] = useState(false);

  const [uploadingEduImage, setUploadingEduImage] = useState(false);
  const [eduImageError, setEduImageError] = useState("");

  const [submitLoading, setSubmitLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [resetKey, setResetKey] = useState(0);

  const isQuestion = slideType === "question";
  const isEducation = slideType === "education";
  const isContent = slideType === "content";

  useEffect(() => {
    const fetchAdmin = async () => {
      try {
        const admin = await GetAdmin();
        setAdminData(admin);
      } catch (error) {
        console.error("خطا در دریافت اطلاعات مدیر:", error);
      }
    };

    fetchAdmin();
  }, []);

  useEffect(() => {
    const fetchLessons = async () => {
      try {
        const res = await getAllLessons();
        setAllLessons(res?.data?.lessons || []);
      } catch (error) {
        console.error("خطا در دریافت دروس:", error);
      }
    };

    fetchLessons();
  }, []);

  useEffect(() => {
    if (!selectedGrade || !selectedBook) {
      setFilteredLessons([]);
      setSelectedLesson("");
      setStatements([]);
      return;
    }

    const filtered = allLessons.filter(
      (lesson) =>
        String(lesson.grade) === String(selectedGrade) &&
        String(lesson.book) === String(selectedBook),
    );

    setFilteredLessons(filtered);
    setSelectedLesson("");
    setStatements([]);
  }, [selectedGrade, selectedBook, allLessons]);

  useEffect(() => {
    if (!selectedLesson) {
      setStatements([]);
      return;
    }

    let cancelled = false;

    const fetchStatements = async () => {
      setStatementsLoading(true);

      try {
        const res = await getStatementsByLesson(selectedLesson);

        if (!cancelled) {
          setStatements(res?.statements || []);
        }
      } catch (error) {
        console.error("خطا در دریافت گزاره‌های آموزشی:", error);

        if (!cancelled) {
          setStatements([]);
        }
      } finally {
        if (!cancelled) {
          setStatementsLoading(false);
        }
      }
    };

    fetchStatements();

    return () => {
      cancelled = true;
    };
  }, [selectedLesson]);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const updateQuestion = (changes) => {
    setCurrentQuestion((prev) => ({
      ...prev,
      ...changes,
    }));
  };

  const handleSlideTypeChange = (type) => {
    setSlideType(type);

    if (type === "question") {
      updateQuestion({
        hasQuestion: true,
        hasEducationalContent: false,
      });
    } else {
      updateQuestion({
        hasQuestion: false,
        hasEducationalContent: true,
        options: [],
        correctAnswer: "",
        guide: "",
        answerDescription: "",
      });
    }
  };

  const handleEduImageUpload = async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    setEduImageError("");

    if (!file.type.startsWith("image/")) {
      setEduImageError("لطفاً یک فایل تصویری انتخاب کنید.");
      event.target.value = "";
      return;
    }

    const MAX_SIZE = 500 * 1024;

    if (file.size > MAX_SIZE) {
      setEduImageError(
        `حجم تصویر نباید بیشتر از ۵۰۰ کیلوبایت باشد. حجم فعلی: ${(
          file.size / 1024
        ).toFixed(0)} کیلوبایت`,
      );

      event.target.value = "";
      return;
    }

    setUploadingEduImage(true);

    try {
      const res = await uploadQuestionsImage(file);

      const url =
        res?.data?.url || res?.data?.imageUrl || res?.data?.data?.url || "";

      if (!url) {
        showToast("آدرس تصویر از سرور دریافت نشد.", "error");
        return;
      }

      updateQuestion({ educationalImage: url });
      showToast("تصویر با موفقیت بارگذاری شد.");
    } catch (error) {
      console.error("خطا در آپلود تصویر:", error);
      showToast("خطا در بارگذاری تصویر.", "error");
    } finally {
      setUploadingEduImage(false);
      event.target.value = "";
    }
  };

  const buildEducationalContent = (question) => {
    let content = question.educationalContent || "";

    if (question.educationalImage) {
      const imageHtml = `
        <p>
          <img
            src="${question.educationalImage}"
            alt="تصویر آموزشی"
            style="max-width:100%;height:auto;"
          />
        </p>
      `;

      content += imageHtml;
    }

    return content;
  };

  const validateSlide = () => {
    if (!currentQuestion.statementId) {
      showToast("لطفاً گزاره آموزشی مرتبط را انتخاب کنید.", "error");
      return false;
    }

    if (isEducation || isContent) {
      const hasText = currentQuestion.educationalContent?.trim();
      const hasImage = currentQuestion.educationalImage?.trim();
      const hasVideo = currentQuestion.videoUrl?.trim();

      if (!hasText && !hasImage && !hasVideo) {
        showToast(
          "لطفاً حداقل یکی از موارد (متن، تصویر یا ویدیو) را وارد کنید.",
          "error",
        );
        return false;
      }

      if (hasVideo) {
        try {
          const url = new URL(currentQuestion.videoUrl);

          if (!["http:", "https:"].includes(url.protocol)) {
            showToast("لینک ویدیو باید با HTTP یا HTTPS باشد.", "error");
            return false;
          }
        } catch {
          showToast("لطفاً یک لینک معتبر برای ویدیو وارد کنید.", "error");
          return false;
        }
      }

      return true;
    }

    if (!currentQuestion.questionText?.trim()) {
      showToast("لطفاً متن سؤال را وارد کنید.", "error");
      return false;
    }

    if (currentQuestion.options.length < 2) {
      showToast("حداقل دو گزینه باید داشته باشید.", "error");
      return false;
    }

    if (currentQuestion.options.some((option) => !option.optionText?.trim())) {
      showToast("لطفاً متن تمام گزینه‌ها را وارد کنید.", "error");
      return false;
    }

    if (
      !currentQuestion.options.some(
        (option) => option.optionId === currentQuestion.correctAnswer,
      )
    ) {
      showToast("لطفاً گزینه صحیح را انتخاب کنید.", "error");
      return false;
    }

    if (!currentQuestion.guide?.trim()) {
      showToast("لطفاً راهنمای سؤال را وارد کنید.", "error");
      return false;
    }

    if (!currentQuestion.answerDescription?.trim()) {
      showToast("لطفاً توضیحات پاسخ را وارد کنید.", "error");
      return false;
    }

    return true;
  };

  const addOption = () => {
    if (currentQuestion.options.length >= 4) {
      showToast("حداکثر چهار گزینه می‌توانید داشته باشید.", "error");
      return;
    }

    const usedIds = currentQuestion.options.map((option) => option.optionId);

    const optionId = ["1", "2", "3", "4"].find((id) => !usedIds.includes(id));

    updateQuestion({
      options: [...currentQuestion.options, { optionId, optionText: "" }],
    });
  };

  const removeOption = (index) => {
    if (currentQuestion.options.length <= 2) {
      showToast("حداقل دو گزینه باید داشته باشید.", "error");
      return;
    }

    setCurrentQuestion((prev) => {
      const removedOption = prev.options[index];

      // حذف گزینه و بازشماره‌گذاری optionIdها
      const options = prev.options
        .filter((_, optionIndex) => optionIndex !== index)
        .map((opt, i) => ({ ...opt, optionId: String(i + 1) }));

      // اگر گزینه حذف‌شده همان پاسخ صحیح بود، پاک شود
      // در غیر این صورت، شناسه جدید گزینه صحیح پیدا شود
      let correctAnswer = prev.correctAnswer;
      if (correctAnswer === removedOption?.optionId) {
        correctAnswer = "";
      } else if (correctAnswer) {
        const newIndex = prev.options
          .filter((_, optionIndex) => optionIndex !== index)
          .findIndex((opt) => opt.optionId === prev.correctAnswer);
        correctAnswer = newIndex !== -1 ? String(newIndex + 1) : "";
      }

      return { ...prev, options, correctAnswer };
    });
  };

  const handleSubmit = async () => {
    if (submitLoading) return;

    if (!selectedGrade) {
      showToast("لطفاً پایه را انتخاب کنید.", "error");
      return;
    }

    if (!selectedBook) {
      showToast("لطفاً کتاب را انتخاب کنید.", "error");
      return;
    }

    if (!selectedLesson) {
      showToast("لطفاً درس را انتخاب کنید.", "error");
      return;
    }

    if (!validateSlide()) return;

    const usedStatementId = currentQuestion.statementId
      ? [currentQuestion.statementId]
      : [];

    const payload = {
      contentType: slideType,
      type: "normal",
      grade: selectedGrade,
      book: selectedBook,
      lessonId: selectedLesson,
      difficulty,
      educationalObjective: usedStatementId,
      isActive: true,

      questions: [
        {
          order: 1,
          hasEducationalContent: !isQuestion,
          educationalContent: !isQuestion
            ? buildEducationalContent(currentQuestion)
            : "",
          hasQuestion: isQuestion,
          questionText: isQuestion ? currentQuestion.questionText || "" : "",
          options: isQuestion ? currentQuestion.options : [],
          correctAnswer: isQuestion
            ? currentQuestion.options.findIndex(
                (option) => option.optionId === currentQuestion.correctAnswer,
              ) + 1
            : null,
          videoUrl: !isQuestion ? currentQuestion.videoUrl || "" : "",
          defaultScore: currentQuestion.defaultScore || 1,
          answerDescription: isQuestion
            ? currentQuestion.answerDescription || ""
            : "",
          guide: isQuestion ? currentQuestion.guide || "" : "",
          statementId: currentQuestion.statementId || null,
        },
      ],
    };

    setSubmitLoading(true);

    try {
      const res = await createhomeWorkQuestion(payload);

      if (res?.status === 201) {
        showToast("بانک محتوا با موفقیت ایجاد شد.");

        // ✅ پایه، کتاب، درس و سطح دشواری ثابت می‌مانند (حذف شدند)
        // setSelectedGrade("");
        // setSelectedBook("");
        // setSelectedLesson("");
        // setDifficulty("medium");
        // setStatements([]); // نیازی نیست، درس تغییر نکرده

        // فقط بخش‌های مربوط به محتوای سوال پاک می‌شوند
        setSlideType("question");

        setCurrentQuestion({
          ...INITIAL_QUESTION,
          options: INITIAL_QUESTION.options.map((option) => ({ ...option })),
        });

        setEduImageError("");
        setResetKey((prev) => prev + 1); // ✅ اضافه کنید
      } else {
        showToast(res?.data?.message || "خطا در ایجاد بانک محتوا.", "error");
      }
    } catch (error) {
      console.error("خطا در ثبت بانک:", error);

      showToast(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "خطا در ارتباط با سرور.",
        "error",
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  if (!adminData) return <Loading />;

  return (
    <div className="w-full flex flex-col items-center">
      {toast && (
        <div
          role="status"
          className={`fixed top-20 left-1/2 -translate-x-1/2 z-[100] max-w-[90vw] px-5 py-3 rounded-xl shadow-lg text-sm font-medium ${
            toast.type === "success"
              ? "bg-green-50 border border-green-200 text-green-700"
              : "bg-red-50 border border-red-200 text-red-700"
          }`}
        >
          {toast.message}
        </div>
      )}

      <Header adminName={adminData.name} />

      <div className="flex flex-col md:flex-row gap-6 justify-between w-full px-4">
        <div className="w-full md:w-1/4 mb-6 lg:mb-0">
          <Navigation />
        </div>

        <div className="w-full md:w-3/4 min-w-0">
          <div className="bg-gray-50 rounded-xl p-4 space-y-5">
            {/* اطلاعات اصلی */}
            <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    پایه *
                  </label>

                  <select
                    value={selectedGrade}
                    onChange={(e) => setSelectedGrade(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-200 outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">انتخاب پایه</option>

                    {Object.entries(GRADE_MAP).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    کتاب *
                  </label>

                  <select
                    value={selectedBook}
                    onChange={(e) => setSelectedBook(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-200 outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">انتخاب کتاب</option>

                    {Object.entries(BOOK_MAP).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  درس *
                </label>

                <select
                  value={selectedLesson}
                  onChange={(e) => setSelectedLesson(e.target.value)}
                  disabled={!selectedGrade || !selectedBook}
                  className="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-200 outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="">انتخاب درس</option>

                  {filteredLessons.map((lesson) => (
                    <option key={lesson._id} value={lesson._id}>
                      {lesson.title}
                    </option>
                  ))}
                </select>

                {selectedGrade &&
                  selectedBook &&
                  filteredLessons.length === 0 && (
                    <p className="mt-1 text-xs text-amber-600">
                      درسی برای پایه و کتاب انتخاب‌شده پیدا نشد.
                    </p>
                  )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  سطح دشواری
                </label>

                <select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-200 outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="easy">آسان</option>
                  <option value="medium">متوسط</option>
                  <option value="hard">دشوار</option>
                </select>
              </div>
            </section>

            {/* انتخاب نوع اسلاید */}
            <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {SLIDE_TYPES.map((type) => {
                  const active = slideType === type.value;

                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => handleSlideTypeChange(type.value)}
                      aria-pressed={active}
                      className={`flex items-start gap-3 p-3 rounded-xl border text-right transition ${
                        active
                          ? type.active
                          : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-bold">
                          {type.label}
                        </span>

                        <span
                          className={`block text-xs mt-1 leading-5 ${
                            active ? "text-white/80" : "text-gray-500"
                          }`}
                        >
                          {type.description}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* گزاره آموزشی */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  گزاره آموزشی مرتبط *
                </label>

                {statementsLoading ? (
                  <div className="py-4 text-center text-sm text-gray-500">
                    در حال دریافت گزاره‌ها...
                  </div>
                ) : !selectedLesson ? (
                  <p className="text-xs text-gray-500 py-2">
                    ابتدا درس را انتخاب کنید.
                  </p>
                ) : statements.length === 0 ? (
                  <p className="text-xs text-gray-500 py-2">
                    گزاره‌ای برای این درس ثبت نشده است.
                  </p>
                ) : (
                  <div className="border border-gray-200 rounded-xl bg-gray-50 p-3 max-h-48 overflow-y-auto">
                    <div className="flex flex-wrap gap-2">
                      {statements.map((statement) => {
                        const active =
                          currentQuestion.statementId === statement._id;

                        return (
                          <button
                            key={statement._id}
                            type="button"
                            onClick={() =>
                              updateQuestion({
                                statementId: statement._id,
                              })
                            }
                            className={`px-3 py-2 rounded-lg text-xs font-medium transition ${
                              active
                                ? "bg-green-600 text-white"
                                : "bg-white text-gray-700 border border-gray-200 hover:border-green-400"
                            }`}
                          >
                            {active && "✓ "}
                            {statement.content}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* ===== فرم آموزش و محتوای درسی ===== */}
              {!isQuestion && (
                <div
                  className={`p-4 rounded-xl border space-y-5 ${
                    isContent
                      ? "bg-emerald-50/40 border-emerald-200"
                      : "bg-purple-50/40 border-purple-200"
                  }`}
                >
                  <div>
                    <h3 className="text-sm font-bold text-gray-800">
                      {isContent ? "ثبت محتوای درسی" : "ثبت محتوای آموزشی"}
                    </h3>

                    <p className="text-xs text-gray-500 mt-1">
                      حداقل یکی از موارد متن، تصویر یا ویدیو را وارد کنید.
                    </p>
                  </div>

                  {/* متن آموزشی — ادیتور همیشه باز */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      متن {isContent ? "محتوای درسی" : "آموزشی"} (اختیاری)
                    </label>

                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <ArticleInlineEditor
                        key={`edu-${resetKey}`}
                        setContent={(content) =>
                          updateQuestion({ educationalContent: content })
                        }
                        initialContent={currentQuestion.educationalContent}
                      />
                    </div>
                  </div>

                  {/* تصویر */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <PhotoIcon className="w-5 h-5 text-blue-500" />
                      تصویر {isContent ? "محتوای درسی" : "آموزشی"} (اختیاری)
                    </label>

                    <div className="flex flex-wrap items-center gap-3">
                      <label
                        className={`relative inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition ${
                          uploadingEduImage
                            ? "bg-gray-100 text-gray-400"
                            : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                        }`}
                      >
                        <PhotoIcon className="w-4 h-4" />

                        {uploadingEduImage
                          ? "در حال بارگذاری..."
                          : "انتخاب تصویر"}

                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleEduImageUpload}
                          disabled={uploadingEduImage}
                          className="sr-only"
                        />
                      </label>

                      <span className="text-xs text-gray-500">
                        حداکثر ۵۰۰ کیلوبایت
                      </span>
                    </div>

                    {eduImageError && (
                      <p className="mt-2 text-sm text-red-600">
                        {eduImageError}
                      </p>
                    )}

                    {currentQuestion.educationalImage && (
                      <div className="mt-3 flex items-start gap-3">
                        <img
                          src={currentQuestion.educationalImage}
                          alt="پیش‌نمایش تصویر"
                          className="w-36 h-28 object-contain rounded-lg border border-gray-200 bg-white"
                        />

                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-green-700 mb-2">
                            تصویر بارگذاری شده است.
                          </p>

                          <button
                            type="button"
                            onClick={() =>
                              updateQuestion({ educationalImage: "" })
                            }
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-red-600 bg-red-50 hover:bg-red-100"
                          >
                            <XMarkIcon className="w-4 h-4" />
                            حذف تصویر
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* لینک ویدیو */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                      <PlayCircleIcon className="w-5 h-5 text-blue-500" />
                      لینک ویدیوی {isContent ? "درسی" : "آموزشی"} (اختیاری)
                    </label>

                    <div className="relative">
                      <input
                        type="url"
                        dir="ltr"
                        value={currentQuestion.videoUrl}
                        onChange={(e) =>
                          updateQuestion({ videoUrl: e.target.value })
                        }
                        placeholder="https://example.com/video"
                        className="w-full px-3 py-2.5 text-sm rounded-xl border border-gray-200 outline-none focus:ring-2 focus:ring-blue-500 text-left bg-white"
                      />

                      {currentQuestion.videoUrl && (
                        <button
                          type="button"
                          onClick={() => updateQuestion({ videoUrl: "" })}
                          title="پاک کردن لینک"
                          className="absolute left-2 top-1/2 -translate-y-1/2 p-1 text-red-500 hover:bg-red-50 rounded-full"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <p className="mt-1 text-xs text-gray-500">
                      آدرس مستقیم یا لینک قابل‌دسترسی ویدیو را وارد کنید.
                    </p>
                  </div>
                </div>
              )}

              {/* ===== فرم سؤال ===== */}
              {isQuestion && (
                <div className="space-y-5">
                  {/* متن سؤال — ادیتور همیشه باز */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      متن سؤال *
                    </label>

                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <ArticleInlineEditor
                        key={`question-${resetKey}`}
                        setContent={(content) =>
                          updateQuestion({ questionText: content })
                        }
                        initialContent={currentQuestion.questionText}
                      />
                    </div>
                  </div>

                  {/* گزینه‌ها — هر کدام با ادیتور همیشه باز */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      گزینه‌ها (حداقل ۲ و حداکثر ۴ گزینه)
                    </label>

                    {currentQuestion.options.map((option, index) => (
                      <div
                        key={option.optionId}
                        className="flex items-start gap-2"
                      >
                        <span className="w-7 pt-3 text-sm font-bold text-gray-600">
                          {option.optionId}
                        </span>

                        <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded-lg overflow-hidden">
                          <ArticleInlineEditor
                            key={`option-${option.optionId}-${resetKey}`} // ✅
                            setContent={(content) => {
                              setCurrentQuestion((prev) => ({
                                ...prev,
                                options: prev.options.map((opt, i) =>
                                  i === index
                                    ? { ...opt, optionText: content }
                                    : opt,
                                ),
                              }));
                            }}
                            initialContent={option.optionText}
                          />
                        </div>

                        {currentQuestion.options.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removeOption(index)}
                            title="حذف گزینه"
                            className="p-2 mt-1 text-red-500 hover:bg-red-50 rounded-lg"
                          >
                            <XMarkIcon className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    ))}

                    {currentQuestion.options.length < 4 && (
                      <button
                        type="button"
                        onClick={addOption}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800"
                      >
                        + افزودن گزینه
                      </button>
                    )}
                  </div>

                  {/* پاسخ صحیح */}
                  <div className="border-t border-gray-200 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      پاسخ صحیح *
                    </label>

                    <div className="flex flex-wrap gap-2">
                      {currentQuestion.options.map((option, index) => {
                        const active =
                          currentQuestion.correctAnswer === option.optionId;

                        return (
                          <button
                            key={option.optionId}
                            type="button"
                            onClick={() =>
                              updateQuestion({
                                correctAnswer: option.optionId,
                              })
                            }
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                              active
                                ? "bg-green-600 text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            گزینه {index + 1}
                            {active && " ✓"}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* راهنما — ادیتور همیشه باز */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      راهنمای سؤال *
                    </label>

                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <ArticleInlineEditor
                        key={`guide-${resetKey}`}
                        setContent={(content) =>
                          updateQuestion({ guide: content })
                        }
                        initialContent={currentQuestion.guide}
                      />
                    </div>
                  </div>

                  {/* توضیحات پاسخ — ادیتور همیشه باز */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      توضیحات پاسخ *
                    </label>

                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <ArticleInlineEditor
                        key={`answer-${resetKey}`}
                        setContent={(content) =>
                          updateQuestion({ answerDescription: content })
                        }
                        initialContent={currentQuestion.answerDescription}
                      />
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* ثبت */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitLoading || uploadingEduImage}
              className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold shadow-sm hover:bg-blue-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitLoading
                ? "در حال ثبت..."
                : isContent
                  ? "ثبت محتوای درسی"
                  : isEducation
                    ? "ثبت محتوای آموزشی"
                    : "ایجاد بانک سؤال"}
            </button>

            <HomeWorksTable userRole={adminData.role} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeWork;
